import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Shield,
  Lock,
  Eye,
  Server,
  Users,
  Globe,
  Cookie,
  Trash2,
  Mail,
  FileText,
  Scale,
  CreditCard,
  AlertTriangle,
} from "lucide-react";
import WhiteLogo from "../../assets/KavoaWhiteLogo.png";
import FooterSection from "../FooterSection";

const sections = [
  { id: "introduction", label: "Introduction" },
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use-information", label: "How We Use Information" },
  { id: "legal-basis", label: "Legal Basis for Processing" },
  { id: "data-sharing", label: "Data Sharing & Third Parties" },
  { id: "attorney-client-privilege", label: "Attorney-Client Privilege" },
  { id: "data-security", label: "Data Security" },
  { id: "data-retention", label: "Data Retention" },
  { id: "your-rights", label: "Your Rights" },
  { id: "international-transfers", label: "International Data Transfers" },
  { id: "cookies", label: "Cookies & Tracking" },
  { id: "childrens-privacy", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full bg-white">
      {/* Header */}
      <header className="bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm mb-8"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>

          <div className="mb-8">
            <img
              src={WhiteLogo}
              alt="Kavoa"
              className="h-10 object-contain"
            />
          </div>

          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-400 text-lg">
            Effective Date: February 1, 2026
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Table of Contents */}
        <div className="bg-slate-50 rounded-2xl p-6 mb-12">
          <h2 className="text-lg font-bold text-slate-900 mb-4">
            Table of Contents
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {sections.map((section, index) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="text-sm text-slate-600 hover:text-brand transition-colors py-1"
              >
                <span
                  className="font-semibold mr-2"
                  style={{ color: "var(--color-primary)" }}
                >
                  {index + 1}.
                </span>
                {section.label}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-12">
          {/* 1. Introduction */}
          <section id="introduction">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Shield
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                1. Introduction
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa Inc. ("we," "us," or "our") operates the Kavoa platform
                at kavoa.io (the "Service"), a cloud-based law firm management
                platform designed for legal professionals. This Privacy Policy
                explains how we collect, use, disclose, and safeguard your
                information when you use our Service, including our website,
                applications, and all related services.
              </p>
              <p>
                We understand that as a law firm management platform, we handle
                uniquely sensitive information -- including data that may be
                subject to attorney-client privilege, work product protections,
                and various professional responsibility obligations. We take
                this responsibility seriously and have designed our privacy
                practices with the heightened confidentiality requirements of
                the legal profession in mind.
              </p>
              <p>
                By accessing or using our Service, you agree to the collection
                and use of information in accordance with this Privacy Policy.
                If you do not agree with the terms of this Privacy Policy,
                please do not access or use the Service.
              </p>
            </div>
          </section>

          {/* 2. Information We Collect */}
          <section id="information-we-collect">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Eye
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                2. Information We Collect
              </h2>
            </div>
            <div className="space-y-6 text-slate-600 leading-relaxed">
              <p>
                We collect several types of information to provide and improve
                our Service. The categories of information we collect depend on
                how you interact with our platform and which features your firm
                uses.
              </p>

              {/* Account Information */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Account Information
                </h3>
                <p className="mb-3">
                  When you register for and use Kavoa, we collect personal
                  information necessary to create and maintain your account:
                </p>
                <ul className="space-y-2 ml-1">
                  {[
                    "Full name and professional title",
                    "Email address and phone number",
                    "Law firm name, address, and jurisdiction(s) of practice",
                    "Bar admission number and licensing jurisdiction (if provided)",
                    "Role within the firm (e.g., partner, associate, paralegal, administrator)",
                    "Profile photo (optional)",
                    "Password (stored using bcrypt hashing -- we never store plaintext passwords)",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Client & Matter Data */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Client & Matter Data
                </h3>
                <div
                  className="border-l-4 pl-4 py-3 bg-brand-light/50 rounded-r-lg mb-3"
                  style={{ borderColor: "var(--color-primary)" }}
                >
                  <p className="text-sm font-semibold text-slate-800">
                    Important: For client and matter data, your law firm is the
                    Data Controller and Kavoa acts strictly as a Data Processor.
                  </p>
                </div>
                <p className="mb-3">
                  Data entered by your firm's users about your clients and
                  legal matters may include:
                </p>
                <ul className="space-y-2 ml-1">
                  {[
                    "Client names, contact details, and identification information",
                    "Matter descriptions, case types, and practice area classifications",
                    "Case notes, legal research, court dates, and deadlines",
                    "Immigration case details (visa types, petition status, USCIS receipt numbers)",
                    "Real estate transaction records (property details, closing dates, parties involved)",
                    "Lead and intake information submitted through your firm's intake processes",
                    "Internal task assignments, status updates, and workflow data",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Financial Data */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Financial Data
                </h3>
                <p className="mb-3">
                  To facilitate billing, invoicing, and trust accounting, we
                  process financial information including:
                </p>
                <ul className="space-y-2 ml-1">
                  {[
                    "Billing rates, time entries, expense records, and invoice details",
                    "Trust account (IOLTA) transactions, balances, and reconciliation records",
                    "Payment processing information via Stripe -- we do NOT store full credit card numbers, CVVs, or complete bank account numbers on our servers",
                    "Subscription and plan billing details for your Kavoa account",
                    "Payment plan configurations and autopay enrollment records",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Usage Data */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Usage Data
                </h3>
                <p className="mb-3">
                  We automatically collect certain information when you access
                  and use the Service:
                </p>
                <ul className="space-y-2 ml-1">
                  {[
                    "Log data (access times, pages viewed, referring URLs)",
                    "Device information (browser type, operating system, screen resolution)",
                    "IP addresses and approximate geographic location",
                    "Feature usage analytics (which tools and modules you use, frequency of use)",
                    "Performance metrics and error logs for service reliability",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Communications Data */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Communications Data
                </h3>
                <p className="mb-3">
                  If your firm uses Kavoa's integrated communication features,
                  we process:
                </p>
                <ul className="space-y-2 ml-1">
                  {[
                    "SMS/text message content and metadata sent through our Twilio integration",
                    "Email content and metadata when using connected email accounts",
                    "Voice call logs, recordings (if enabled by your firm), and call metadata",
                    "Internal conversation threads and notes within the platform",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Documents */}
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Documents
                </h3>
                <p>
                  Files uploaded to Kavoa -- including legal documents,
                  contracts, court filings, templates, and e-signature
                  documents -- are stored securely using encrypted cloud
                  storage. We do not access, read, or analyze the content of
                  your documents except as strictly necessary to provide
                  technical service delivery (e.g., generating document
                  previews, processing e-signature requests).
                </p>
              </div>
            </div>
          </section>

          {/* 3. How We Use Information */}
          <section id="how-we-use-information">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <FileText
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                3. How We Use Information
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We use the information we collect for the following purposes,
                always in a manner consistent with our role as a data processor
                for your firm's client data:
              </p>
              <ul className="space-y-2 ml-1">
                {[
                  {
                    title: "Service Delivery",
                    desc: "To provide, operate, and maintain the Kavoa platform, including matter management, billing, trust accounting, calendar, document management, communications, immigration case tracking, and real estate transaction management.",
                  },
                  {
                    title: "Billing & Payments",
                    desc: "To process subscription payments, manage your account billing, facilitate client payments through Stripe, and maintain accurate trust accounting records.",
                  },
                  {
                    title: "Customer Support",
                    desc: "To respond to your inquiries, troubleshoot issues, and provide technical assistance. Support staff access to your account data is logged and limited to what is necessary to resolve your request.",
                  },
                  {
                    title: "Product Improvement",
                    desc: "To understand how our Service is used and improve functionality, user experience, and performance. We use aggregated and anonymized usage data for this purpose -- never the substance of your legal data.",
                  },
                  {
                    title: "Security & Fraud Prevention",
                    desc: "To monitor for suspicious activity, prevent unauthorized access, detect security threats, and protect the integrity of our Service and your data.",
                  },
                  {
                    title: "Legal Compliance",
                    desc: "To comply with applicable laws, regulations, legal processes, or governmental requests, and to enforce our Terms of Service.",
                  },
                  {
                    title: "Communications",
                    desc: "To send you service-related notices (e.g., maintenance windows, security alerts, billing notifications). We do not send unsolicited marketing emails without your explicit consent.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>
                      <strong className="text-slate-800">{item.title}:</strong>{" "}
                      {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 4. Legal Basis for Processing */}
          <section id="legal-basis">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Scale
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                4. Legal Basis for Processing
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                For users in the European Economic Area (EEA) and other
                jurisdictions that require a legal basis for processing
                personal data, we rely on the following grounds:
              </p>
              <ul className="space-y-3 ml-1">
                {[
                  {
                    title: "Contract Performance",
                    desc: "Processing your account information and usage data is necessary to perform our contract with you (our Terms of Service) and deliver the Kavoa platform services you have subscribed to.",
                  },
                  {
                    title: "Legitimate Interests",
                    desc: "We process certain data based on our legitimate interests, including improving our Service, ensuring security, preventing fraud, and conducting anonymized analytics. We balance these interests against your privacy rights and only rely on this basis when our interests are not overridden by your rights.",
                  },
                  {
                    title: "Consent",
                    desc: "Where required by law, we obtain your explicit consent before processing certain types of data, such as optional analytics cookies or marketing communications. You may withdraw consent at any time by contacting us or adjusting your account settings.",
                  },
                  {
                    title: "Legal Obligations",
                    desc: "We may process your information to comply with applicable legal obligations, such as tax reporting requirements, responding to lawful court orders or subpoenas, or meeting data protection regulatory requirements.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>
                      <strong className="text-slate-800">{item.title}:</strong>{" "}
                      {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
              <p>
                For client and matter data entered by your firm, the law firm
                is the data controller and determines the legal basis for
                processing that data. Kavoa processes such data solely on the
                firm's behalf and in accordance with the firm's instructions.
              </p>
            </div>
          </section>

          {/* 5. Data Sharing & Third Parties */}
          <section id="data-sharing">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Users
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                5. Data Sharing & Third Parties
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <div
                className="border-l-4 pl-4 py-3 bg-brand-light/50 rounded-r-lg"
                style={{ borderColor: "var(--color-primary)" }}
              >
                <p className="text-sm font-semibold text-slate-800">
                  We do NOT sell, rent, or trade your personal information or
                  your firm's client data to any third party. Ever.
                </p>
              </div>
              <p>
                We share information only in the following limited
                circumstances, and all third-party service providers are bound
                by Data Processing Agreements (DPAs) that require them to
                protect your data to the same standards we maintain:
              </p>
              <ul className="space-y-3 ml-1">
                {[
                  {
                    title: "Cloud Infrastructure Providers",
                    desc: "We use industry-leading cloud hosting services to store and process your data. Our infrastructure providers maintain SOC 2 Type II compliance, ISO 27001 certification, and undergo regular security audits.",
                  },
                  {
                    title: "Payment Processor (Stripe)",
                    desc: "Payment card data is processed directly by Stripe, a PCI DSS Level 1 certified payment processor. Card details are tokenized by Stripe and never touch our servers. We only receive limited payment confirmation data necessary for billing records.",
                  },
                  {
                    title: "Email Service Providers",
                    desc: "We use trusted email delivery services for transactional emails (account notifications, billing receipts, system alerts). These providers process only the minimum data necessary for email delivery.",
                  },
                  {
                    title: "Communications Providers (Twilio)",
                    desc: "If your firm uses integrated SMS, voice, or phone features, we use Twilio to facilitate these communications. Twilio processes message and call data in accordance with their privacy commitments and our DPA.",
                  },
                  {
                    title: "Analytics (Anonymized Only)",
                    desc: "We may use analytics tools to understand aggregate usage patterns and improve our Service. All analytics data is anonymized and aggregated -- we never share identifiable client or matter data with analytics providers.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>
                      <strong className="text-slate-800">{item.title}:</strong>{" "}
                      {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
              <p>
                We may also disclose information if required by law, court
                order, or governmental authority, or if we believe in good
                faith that such disclosure is necessary to protect our rights,
                your safety, or the safety of others. In such cases, we will
                notify you to the extent permitted by law.
              </p>
            </div>
          </section>

          {/* 6. Attorney-Client Privilege & Legal Data */}
          <section id="attorney-client-privilege">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Lock
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                6. Attorney-Client Privilege & Legal Data
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <div
                className="border-l-4 pl-4 py-3 bg-brand-light/50 rounded-r-lg"
                style={{ borderColor: "var(--color-primary)" }}
              >
                <p className="text-sm font-semibold text-slate-800">
                  Kavoa recognizes and respects that data stored on our
                  platform may be subject to attorney-client privilege, work
                  product doctrine, and other legal protections.
                </p>
              </div>
              <p>
                We acknowledge that law firms using Kavoa entrust us with data
                that is among the most sensitive categories of information in
                existence. Attorney-client communications, legal strategies,
                case assessments, and work product are foundational to the
                justice system, and we are committed to safeguarding them.
              </p>
              <p>
                <strong className="text-slate-800">
                  Our commitment regarding privileged data:
                </strong>
              </p>
              <ul className="space-y-2 ml-1">
                {[
                  "We act strictly as a data processor. The law firm retains full control over all client and matter data as the data controller.",
                  "We do not access, review, analyze, mine, or otherwise examine the substance or content of legal data except as strictly necessary for technical service delivery (e.g., database operations, backup processes, troubleshooting at the firm's explicit request).",
                  "All Kavoa employees and contractors are bound by strict confidentiality obligations and non-disclosure agreements that specifically address the privileged nature of legal data.",
                  "We employ role-based access controls internally, ensuring that only authorized personnel with a legitimate technical need can access production systems containing customer data.",
                  "Employee access to customer data is logged and audited. We maintain detailed access logs that can be made available to your firm upon request.",
                  "In the event of a legal demand (subpoena, court order) directed at Kavoa for your firm's data, we will promptly notify you (to the extent legally permitted) so that you can assert any applicable privileges or protections before any disclosure.",
                  "We do not use the content of your legal data for training machine learning models, advertising purposes, or any use beyond providing the Service.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 7. Data Security */}
          <section id="data-security">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Server
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                7. Data Security
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We implement comprehensive security measures designed to
                protect your information from unauthorized access, alteration,
                disclosure, or destruction. Given the sensitive nature of legal
                data, our security program exceeds industry standards for SaaS
                platforms:
              </p>
              <ul className="space-y-3 ml-1">
                {[
                  {
                    title: "Encryption at Rest",
                    desc: "All data stored on our servers is encrypted using AES-256 encryption, the same standard used by financial institutions and government agencies.",
                  },
                  {
                    title: "Encryption in Transit",
                    desc: "All data transmitted between your browser and our servers is protected using TLS 1.2 or higher. We enforce HTTPS on all connections and employ HTTP Strict Transport Security (HSTS).",
                  },
                  {
                    title: "Access Controls",
                    desc: "We enforce strict role-based access controls (RBAC) both within the application and our internal infrastructure. Multi-factor authentication is required for all employee access to production systems.",
                  },
                  {
                    title: "Security Audits",
                    desc: "We conduct regular security assessments, including vulnerability scanning and penetration testing by independent third-party security firms.",
                  },
                  {
                    title: "Employee Security",
                    desc: "All employees undergo background checks and receive security awareness training. Access to customer data is limited to personnel who require it for their role and is subject to the principle of least privilege.",
                  },
                  {
                    title: "Incident Response",
                    desc: "We maintain a documented incident response plan. In the event of a data breach that affects your information, we will notify affected customers within 72 hours and provide details about the nature of the breach, the data involved, and steps being taken to remediate.",
                  },
                  {
                    title: "Infrastructure Security",
                    desc: "Our infrastructure includes network firewalls, intrusion detection systems, DDoS protection, automated threat monitoring, and regular security patching.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>
                      <strong className="text-slate-800">{item.title}:</strong>{" "}
                      {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
              <p>
                While we implement robust security measures, no method of
                electronic storage or transmission is 100% secure. We
                continuously evaluate and improve our security practices to
                address evolving threats.
              </p>
            </div>
          </section>

          {/* 8. Data Retention */}
          <section id="data-retention">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Trash2
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                8. Data Retention
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We retain your data only for as long as necessary to provide
                the Service and fulfill the purposes described in this Privacy
                Policy. Our retention practices are designed to balance
                operational needs with your right to have data deleted:
              </p>
              <ul className="space-y-3 ml-1">
                {[
                  {
                    title: "Active Accounts",
                    desc: "All data associated with your account is retained for the duration of your active subscription. You maintain full control over your data during this period, including the ability to export, modify, or delete individual records.",
                  },
                  {
                    title: "After Cancellation",
                    desc: "Following account cancellation, we retain your data for a 90-day grace period. This allows for account reactivation without data loss in case of accidental cancellation or if you decide to return. During this period, your data remains encrypted and is not actively processed.",
                  },
                  {
                    title: "Permanent Deletion",
                    desc: "After the 90-day grace period, all of your firm's data -- including client records, matter data, documents, billing records, communications, and account information -- is permanently and irreversibly deleted from our primary systems.",
                  },
                  {
                    title: "Backup Purging",
                    desc: "Data contained in encrypted backups is purged within 30 days following permanent deletion from primary systems. Backups are encrypted and access-restricted even before purging.",
                  },
                  {
                    title: "Immediate Deletion Requests",
                    desc: "You may request immediate deletion of your data at any time by contacting support@kavoa.io. Upon verification of your identity and authorization, we will initiate permanent deletion within 5 business days, with backup purging completed within 30 days thereafter.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>
                      <strong className="text-slate-800">{item.title}:</strong>{" "}
                      {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
              <p>
                Certain limited data may be retained longer if required by law
                (e.g., billing records for tax compliance) or to resolve
                disputes. We will inform you of any such obligations upon
                request.
              </p>
            </div>
          </section>

          {/* 9. Your Rights */}
          <section id="your-rights">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Shield
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                9. Your Rights
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Depending on your jurisdiction, you may have the following
                rights regarding your personal data. We are committed to
                honoring these rights and making it easy for you to exercise
                them:
              </p>
              <ul className="space-y-3 ml-1">
                {[
                  {
                    title: "Right of Access",
                    desc: "You have the right to request a copy of the personal data we hold about you, including information about how it is processed and with whom it is shared.",
                  },
                  {
                    title: "Right to Rectification",
                    desc: "You may request that we correct any inaccurate or incomplete personal data. You can also update most of your information directly through your account settings.",
                  },
                  {
                    title: "Right to Erasure (Right to be Forgotten)",
                    desc: "You can request the deletion of your personal data. We will comply unless we have a legitimate legal basis to retain it (e.g., legal compliance obligations).",
                  },
                  {
                    title: "Right to Data Portability",
                    desc: "You have the right to receive your personal data in a structured, commonly used, machine-readable format (e.g., JSON or CSV), and to transmit that data to another service provider.",
                  },
                  {
                    title: "Right to Restriction of Processing",
                    desc: "You may request that we restrict the processing of your personal data in certain circumstances, such as when you contest the accuracy of the data or object to our processing.",
                  },
                  {
                    title: "Right to Object",
                    desc: "You have the right to object to the processing of your personal data for certain purposes, including direct marketing and processing based on legitimate interests.",
                  },
                  {
                    title: "Right to Withdraw Consent",
                    desc: "Where processing is based on your consent, you may withdraw that consent at any time. Withdrawal does not affect the lawfulness of processing that occurred before the withdrawal.",
                  },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>
                      <strong className="text-slate-800">{item.title}:</strong>{" "}
                      {item.desc}
                    </span>
                  </li>
                ))}
              </ul>
              <div
                className="border-l-4 pl-4 py-3 bg-brand-light/50 rounded-r-lg"
                style={{ borderColor: "var(--color-primary)" }}
              >
                <p className="text-sm text-slate-700">
                  <strong className="text-slate-800">
                    How to exercise your rights:
                  </strong>{" "}
                  To submit a data rights request, please email us at{" "}
                  <a
                    href="mailto:support@kavoa.io"
                    className="font-semibold underline"
                    style={{ color: "var(--color-primary)" }}
                  >
                    support@kavoa.io
                  </a>{" "}
                  with the subject line "Data Rights Request." We will verify
                  your identity and respond within 30 days. If we need
                  additional time, we will notify you of the extension and the
                  reasons for the delay.
                </p>
              </div>
            </div>
          </section>

          {/* 10. International Data Transfers */}
          <section id="international-transfers">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Globe
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                10. International Data Transfers
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa's primary infrastructure is located in the United States.
                If you access the Service from outside the United States, your
                information may be transferred to, stored, and processed in
                the United States or other countries where our service
                providers maintain facilities.
              </p>
              <p>
                For users in the European Economic Area (EEA), United Kingdom,
                or Switzerland, we implement the following safeguards to ensure
                your data receives an adequate level of protection when
                transferred outside your jurisdiction:
              </p>
              <ul className="space-y-2 ml-1">
                {[
                  "Standard Contractual Clauses (SCCs) approved by the European Commission are incorporated into our agreements with data sub-processors that process EEA personal data outside the EEA.",
                  "We conduct transfer impact assessments to evaluate the legal framework of the destination country and implement supplementary measures where necessary.",
                  "All international data transfers are encrypted in transit and at rest, regardless of the destination country.",
                  "We contractually require our sub-processors to maintain privacy protections consistent with applicable data protection laws.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>
                If you have questions about international data transfers or
                wish to receive a copy of the safeguards we have in place,
                please contact us at{" "}
                <a
                  href="mailto:support@kavoa.io"
                  className="font-semibold underline"
                  style={{ color: "var(--color-primary)" }}
                >
                  support@kavoa.io
                </a>
                .
              </p>
            </div>
          </section>

          {/* 11. Cookies & Tracking */}
          <section id="cookies">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Cookie
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                11. Cookies & Tracking
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We take a privacy-first approach to cookies and tracking
                technologies. Our use of cookies is minimal and focused on
                providing a functional, secure experience:
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Essential Cookies (Always Active)
                  </h3>
                  <p>
                    These cookies are strictly necessary for the Service to
                    function and cannot be disabled. They include:
                  </p>
                  <ul className="space-y-2 ml-1 mt-2">
                    {[
                      "Session cookies to maintain your authenticated session",
                      "Authentication tokens to keep you securely logged in",
                      "Security cookies to prevent cross-site request forgery (CSRF)",
                      "Preference cookies to remember your display settings (e.g., theme, sidebar state)",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span
                          className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: "var(--color-primary)" }}
                        />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    Optional Analytics Cookies (Consent Required)
                  </h3>
                  <p>
                    We may use anonymized analytics cookies to understand
                    aggregate usage patterns and improve our Service. These
                    cookies are only placed with your explicit consent and can
                    be disabled at any time through your browser settings or by
                    contacting us.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">
                    What We Do NOT Use
                  </h3>
                  <div
                    className="border-l-4 pl-4 py-3 bg-brand-light/50 rounded-r-lg"
                    style={{ borderColor: "var(--color-primary)" }}
                  >
                    <p className="text-sm font-semibold text-slate-800">
                      We do not use third-party advertising cookies, retargeting
                      pixels, social media tracking scripts, or any other
                      tracking technology designed to follow you across other
                      websites. Your legal data remains private.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 12. Children's Privacy */}
          <section id="childrens-privacy">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <AlertTriangle
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                12. Children's Privacy
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Kavoa is a professional business tool designed exclusively for
                use by licensed attorneys, law firm staff, and legal
                professionals. Our Service is not directed at individuals under
                the age of 18, and we do not knowingly collect personal
                information from children or minors.
              </p>
              <p>
                If we become aware that we have inadvertently collected
                personal data from a person under 18, we will take immediate
                steps to delete that information from our systems. If you
                believe that a minor has provided us with personal information,
                please contact us at{" "}
                <a
                  href="mailto:support@kavoa.io"
                  className="font-semibold underline"
                  style={{ color: "var(--color-primary)" }}
                >
                  support@kavoa.io
                </a>{" "}
                so that we can take appropriate action.
              </p>
              <p>
                Note that law firms may store information about minor clients
                (e.g., in immigration or family law matters) as part of their
                legal practice data. In such cases, the law firm is the data
                controller for that information, and Kavoa processes it solely
                as a data processor under the firm's direction and in
                accordance with this Privacy Policy's data protection
                standards.
              </p>
            </div>
          </section>

          {/* 13. Changes to This Policy */}
          <section id="changes">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <FileText
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                13. Changes to This Policy
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time to reflect
                changes in our practices, technology, legal requirements, or
                other factors. We are committed to transparency in any changes
                we make:
              </p>
              <ul className="space-y-2 ml-1">
                {[
                  "For material changes that affect how we collect, use, or share your data, we will provide at least 30 days' advance notice via email to the address associated with your account.",
                  "The updated policy will be posted on our website with a revised \"Effective Date\" at the top of the page.",
                  "We will maintain an archive of previous versions of this policy, available upon request.",
                  "Your continued use of the Service after the effective date of a revised policy constitutes your acceptance of the updated terms.",
                  "If you disagree with any changes, you may close your account and request deletion of your data before the changes take effect.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: "var(--color-primary)" }}
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* 14. Contact Us */}
          <section id="contact">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#E8F5E9" }}
              >
                <Mail
                  className="h-5 w-5"
                  style={{ color: "var(--color-primary)" }}
                />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                14. Contact Us
              </h2>
            </div>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our data practices, we encourage you to
                reach out. We take all privacy inquiries seriously and aim to
                respond within 5 business days.
              </p>
              <div className="bg-slate-50 rounded-2xl p-6 space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">
                    Data Protection & Privacy Inquiries
                  </p>
                  <p className="text-sm">
                    Email:{" "}
                    <a
                      href="mailto:support@kavoa.io"
                      className="font-semibold underline"
                      style={{ color: "var(--color-primary)" }}
                    >
                      support@kavoa.io
                    </a>
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">
                    Company
                  </p>
                  <p className="text-sm">
                    Kavoa Inc.
                    <br />
                    Incorporated in Delaware, USA
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800 mb-1">
                    Data Rights Requests
                  </p>
                  <p className="text-sm">
                    To exercise any of your data rights (access, deletion,
                    portability, etc.), email{" "}
                    <a
                      href="mailto:support@kavoa.io"
                      className="font-semibold underline"
                      style={{ color: "var(--color-primary)" }}
                    >
                      support@kavoa.io
                    </a>{" "}
                    with the subject line "Data Rights Request." Please include
                    your full name, the email address associated with your
                    Kavoa account, and a description of your request. We will
                    verify your identity and respond within 30 days.
                  </p>
                </div>
              </div>
              <p>
                If you are located in the EEA and believe that our processing
                of your personal data violates applicable data protection law,
                you have the right to lodge a complaint with your local
                supervisory authority. However, we encourage you to contact us
                first so that we can address your concerns directly.
              </p>
            </div>
          </section>
        </div>
      </main>

      <FooterSection />
    </div>
  );
};

export default PrivacyPolicy;
